import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import pb from '@/lib/pocketbase'
import { useAuthStore } from '@/stores/authStore'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import StatCard from '@/components/ui/StatCard'
import type { Kegiatan } from '@/types/pocketbase'

export default function StadionDashboardPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const assignedIds = useAuthStore((s) => s.assignedKegiatanIds)

  const { data: kegiatanResult, isLoading } = useQuery({
    queryKey: ['stadion', 'kegiatan', assignedIds],
    queryFn: async () => {
      if (assignedIds.length === 0) return null
      const filter = assignedIds.map((id) => `id = "${id}"`).join(' || ')
      return pb.collection('kegiatan').getList(1, 50, {
        filter,
        sort: '-created',
      })
    },
    enabled: assignedIds.length > 0,
  })

  const { data: laporanCount } = useQuery({
    queryKey: ['stadion', 'laporan-count', assignedIds],
    queryFn: async () => {
      if (assignedIds.length === 0) return 0
      const filter = assignedIds.map((id) => `kegiatan = "${id}"`).join(' || ')
      const result = await pb.collection('kegiatan_laporan').getList(1, 1, {
        filter,
      })
      return result.totalItems
    },
    enabled: assignedIds.length > 0,
  })

  const { data: galeriCount } = useQuery({
    queryKey: ['stadion', 'galeri-count', assignedIds],
    queryFn: async () => {
      if (assignedIds.length === 0) return 0
      const filter = assignedIds.map((id) => `kegiatan = "${id}"`).join(' || ')
      const result = await pb.collection('kegiatan_galeri').getList(1, 1, {
        filter,
      })
      return result.totalItems
    },
    enabled: assignedIds.length > 0,
  })

  const { data: rapatCount } = useQuery({
    queryKey: ['stadion', 'rapat-count', assignedIds],
    queryFn: async () => {
      if (assignedIds.length === 0) return 0
      const filter = assignedIds.map((id) => `kegiatan = "${id}"`).join(' || ')
      const result = await pb.collection('kegiatan_rapat').getList(1, 1, {
        filter,
      })
      return result.totalItems
    },
    enabled: assignedIds.length > 0,
  })

  const items = (kegiatanResult?.items || []) as unknown as Kegiatan[]
  const aktif = items.filter((k) => k.status === 'aktif').length
  const draft = items.filter((k) => k.status === 'draft').length

  const greeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Selamat Pagi'
    if (hour < 15) return 'Selamat Siang'
    if (hour < 18) return 'Selamat Sore'
    return 'Selamat Malam'
  }

  if (assignedIds.length === 0) {
    return (
      <div className="py-4">
        <EmptyState
          title="Belum ada kegiatan"
          description="Anda belum ditugaskan pada kegiatan manapun."
        />
      </div>
    )
  }

  return (
    <div className="py-4 space-y-4">
      <div className="mb-2">
        <h1 className="text-xl font-bold text-primary">{greeting()},</h1>
        <p className="text-base font-semibold text-text-primary">{user?.name}</p>
        <p className="text-xs text-text-secondary">Stadion — {items.length} kegiatan ditugaskan</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <StatCard number={items.length} label="Total" color="primary" />
        <StatCard number={aktif} label="Aktif" color="success" />
        <StatCard number={draft} label="Draft" color="warning" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Card className="text-center">
          <p className="text-2xl font-bold text-info">{laporanCount || 0}</p>
          <p className="text-xs text-text-secondary">Laporan</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-primary">{rapatCount || 0}</p>
          <p className="text-xs text-text-secondary">Rapat</p>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          onClick={() => navigate('/laporan/new')}
        >
          Input Laporan
        </Button>
        <Button
          variant="outline"
          onClick={() => items[0] && navigate(`/kegiatan/${items[0].id}`)}
        >
          Upload Foto
        </Button>
      </div>

      <div>
        <h2 className="text-sm font-bold text-text-secondary mb-2">KEGIATAN ANDA</h2>
        {items.length === 0 ? (
          <EmptyState title="Tidak ada kegiatan" />
        ) : (
          items.map((k) => (
            <Card
              key={k.id}
              className="mb-2"
              onClick={() => navigate(`/kegiatan/${k.id}`)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <Badge variant={k.status === 'aktif' ? 'green' : 'yellow'} className="mb-1">
                    {k.status}
                  </Badge>
                  <h3 className="text-sm font-bold truncate">{k.judul}</h3>
                  <p className="text-xs text-text-secondary truncate">{k.lokasi}</p>
                </div>
                <svg className="w-5 h-5 text-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}
